import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Clock, 
  Plus, 
  Trash2,
  Save,
  Calendar,
  Ban,
  Link2,
  Unlink,
  CheckCircle,
  MessageSquare,
  ExternalLink,
  RefreshCw,
  Copy,
  Share2
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS = [
  { value: 0, label: 'Lunes' },
  { value: 1, label: 'Martes' },
  { value: 2, label: 'Miércoles' },
  { value: 3, label: 'Jueves' },
  { value: 4, label: 'Viernes' },
  { value: 5, label: 'Sábado' },
  { value: 6, label: 'Domingo' },
];

const AvailabilityPage = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [rules, setRules] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [newBlock, setNewBlock] = useState({
    block_date_start: '',
    block_date_end: '',
    start_time: '09:00',
    end_time: '18:00',
    reason: ''
  });

  // Google Calendar state
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState({
    connected: false,
    email: null
  });
  const [googleCalendarConfig, setGoogleCalendarConfig] = useState({
    configured: false,
    client_id: ''
  });
  const [showGoogleConfigDialog, setShowGoogleConfigDialog] = useState(false);
  const [googleCredentials, setGoogleCredentials] = useState({
    client_id: '',
    client_secret: ''
  });
  const [savingGoogleConfig, setSavingGoogleConfig] = useState(false);
  const [syncingCalendar, setSyncingCalendar] = useState(false);

  // WhatsApp state
  const [whatsappStatus, setWhatsappStatus] = useState({
    connected: false,
    qr_code: null,
    phone_number: null
  });

  // Clinic config state
  const [clinicConfig, setClinicConfig] = useState({
    phone_number: '',
    clinic_name: '',
    doctor_name: '',
    specialty: ''
  });
  const [savingClinicConfig, setSavingClinicConfig] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [rulesRes, blockedRes] = await Promise.all([
        axios.get(`${API}/availability/rules`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/availability/blocked`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const existingRules = rulesRes.data;
      const allRules = WEEKDAYS.map(day => {
        const existing = existingRules.find(r => r.weekday === day.value);
        return existing || {
          weekday: day.value,
          start_time: '09:00',
          end_time: '18:00',
          appointment_duration_minutes: 10,
          is_active: day.value < 5
        };
      });
      
      setRules(allRules);
      setBlockedSlots(blockedRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchGoogleCalendarStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar/google/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleCalendarStatus(response.data);
    } catch (error) {
      console.error('Error fetching Google Calendar status:', error);
    }
  }, [token]);

  const fetchGoogleCalendarConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar/google/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleCalendarConfig(response.data);
    } catch (error) {
      console.error('Error fetching Google Calendar config:', error);
    }
  }, [token]);

  const fetchWhatsAppStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWhatsappStatus(response.data);
    } catch (error) {
      console.error('Error fetching WhatsApp status:', error);
    }
  }, [token]);

  const fetchClinicConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/clinic/config`);
      setClinicConfig(response.data);
    } catch (error) {
      console.error('Error fetching clinic config:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchGoogleCalendarStatus();
    fetchGoogleCalendarConfig();
    fetchWhatsAppStatus();
    fetchClinicConfig();
    
    // Check for callback params
    if (searchParams.get('calendar_connected') === 'true') {
      fetchGoogleCalendarStatus();
    }
  }, [token, searchParams, fetchData, fetchGoogleCalendarStatus, fetchGoogleCalendarConfig, fetchWhatsAppStatus, fetchClinicConfig]);

  const saveClinicConfig = async () => {
    setSavingClinicConfig(true);
    try {
      await axios.put(`${API}/clinic/config`, clinicConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Configuración guardada exitosamente');
    } catch (error) {
      console.error('Error saving clinic config:', error);
      alert('Error al guardar la configuración');
    } finally {
      setSavingClinicConfig(false);
    }
  };

  const saveGoogleCalendarConfig = async (e) => {
    e.preventDefault();
    if (!googleCredentials.client_id || !googleCredentials.client_secret) {
      alert('Por favor, ingresa ambas credenciales');
      return;
    }
    
    setSavingGoogleConfig(true);
    try {
      await axios.post(`${API}/calendar/google/config`, googleCredentials, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowGoogleConfigDialog(false);
      setGoogleCredentials({ client_id: '', client_secret: '' });
      fetchGoogleCalendarConfig();
      alert('Credenciales guardadas exitosamente');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al guardar credenciales');
    } finally {
      setSavingGoogleConfig(false);
    }
  };

  const deleteGoogleCalendarConfig = async () => {
    if (!window.confirm('¿Estás seguro de eliminar la configuración de Google Calendar? Esto desconectará tu cuenta.')) return;
    
    try {
      await axios.delete(`${API}/calendar/google/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleCalendarConfig({ configured: false, client_id: '' });
      setGoogleCalendarStatus({ connected: false, email: null });
      alert('Configuración eliminada');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar configuración');
    }
  };

  const handleRuleChange = (weekday, field, value) => {
    setRules(rules.map(rule => 
      rule.weekday === weekday ? { ...rule, [field]: value } : rule
    ));
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      for (const rule of rules) {
        await axios.post(`${API}/availability/rules`, rule, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      alert('Configuración guardada exitosamente');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleAddBlock = async (e) => {
    e.preventDefault();
    try {
      // Get dates directly from input values (YYYY-MM-DD format)
      const startDateStr = newBlock.block_date_start;
      const endDateStr = newBlock.block_date_end || newBlock.block_date_start;
      
      // Create a list of dates to block
      const datesToBlock = [];
      
      // Parse dates manually to avoid timezone issues
      const [startYear, startMonth, startDay] = startDateStr.split('-').map(Number);
      const [endYear, endMonth, endDay] = endDateStr.split('-').map(Number);
      
      let currentDate = new Date(startYear, startMonth - 1, startDay, 12, 0, 0); // Use noon to avoid timezone issues
      const endDate = new Date(endYear, endMonth - 1, endDay, 12, 0, 0);
      
      while (currentDate <= endDate) {
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        datesToBlock.push(`${year}-${month}-${day}`);
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Create all blocks
      for (const date of datesToBlock) {
        await axios.post(`${API}/availability/blocked`, {
          block_date: date,
          start_time: newBlock.start_time,
          end_time: newBlock.end_time,
          reason: newBlock.reason
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setShowBlockDialog(false);
      setNewBlock({ block_date_start: '', block_date_end: '', start_time: '09:00', end_time: '18:00', reason: '' });
      fetchData();
      toast.success(`${datesToBlock.length} día(s) bloqueado(s) exitosamente`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear bloqueo');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm('¿Eliminar este bloqueo?')) return;
    try {
      await axios.delete(`${API}/availability/blocked/${blockId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar bloqueo');
    }
  };

  const connectGoogleCalendar = async () => {
    try {
      const response = await axios.get(`${API}/oauth/calendar/login`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al conectar con Google Calendar');
    }
  };

  const disconnectGoogleCalendar = async () => {
    if (!window.confirm('¿Desconectar Google Calendar?')) return;
    try {
      await axios.delete(`${API}/calendar/google/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleCalendarStatus({ connected: false, email: null });
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al desconectar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-dots flex gap-2">
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="availability-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#0B3B71]">Configuración</h1>
          <p className="text-slate-500 mt-1">Horarios, integraciones y canales de comunicación</p>
        </div>
        <Button 
          onClick={saveRules}
          className="bg-[#0891B2] hover:bg-[#0e7490]"
          disabled={saving}
          data-testid="save-availability-button"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="schedule">Horarios</TabsTrigger>
          <TabsTrigger value="integrations">Integraciones</TabsTrigger>
          <TabsTrigger value="channels">Canales</TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-[#0891B2]" />
                Información de la Clínica
              </CardTitle>
              <CardDescription>
                Configura la información básica que se muestra en la página pública
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Nombre del Doctor</Label>
                  <Input
                    value={clinicConfig.doctor_name}
                    onChange={(e) => setClinicConfig({ ...clinicConfig, doctor_name: e.target.value })}
                    placeholder="Dr. Juan Pérez"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Especialidad</Label>
                  <Input
                    value={clinicConfig.specialty}
                    onChange={(e) => setClinicConfig({ ...clinicConfig, specialty: e.target.value })}
                    placeholder="Médico Oftalmólogo"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nombre de la Clínica</Label>
                  <Input
                    value={clinicConfig.clinic_name}
                    onChange={(e) => setClinicConfig({ ...clinicConfig, clinic_name: e.target.value })}
                    placeholder="Clínica Oftalmológica"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono (para botón "Llamar")</Label>
                  <Input
                    value={clinicConfig.phone_number}
                    onChange={(e) => setClinicConfig({ ...clinicConfig, phone_number: e.target.value })}
                    placeholder="+502 1234-5678"
                  />
                  <p className="text-xs text-slate-500">Este número se usará en el botón "Llamar" de la página principal</p>
                </div>
              </div>
              <div className="pt-4">
                <Button 
                  onClick={saveClinicConfig} 
                  disabled={savingClinicConfig}
                  className="bg-[#0891B2] hover:bg-[#0e7490]"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {savingClinicConfig ? 'Guardando...' : 'Guardar Configuración'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Agenda Compacta - Link Compartible */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Share2 className="h-5 w-5 text-[#0891B2]" />
                Agenda Compacta
              </CardTitle>
              <CardDescription>
                Comparte este link con tus pacientes para que agenden sus citas de forma rápida y sencilla
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Input
                    id="agenda-link"
                    value={`${window.location.origin}/agendar`}
                    readOnly
                    className="pr-24 bg-slate-50 font-mono text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      const link = `${window.location.origin}/agendar`;
                      const textArea = document.createElement('textarea');
                      textArea.value = link;
                      textArea.style.position = 'fixed';
                      textArea.style.left = '-9999px';
                      document.body.appendChild(textArea);
                      textArea.select();
                      try {
                        document.execCommand('copy');
                        alert('Link copiado al portapapeles');
                      } catch (err) {
                        // Fallback: select the input
                        const input = document.getElementById('agenda-link');
                        input.select();
                        alert('Selecciona el link y presiona Ctrl+C para copiar');
                      }
                      document.body.removeChild(textArea);
                    }}
                    className="flex-1 sm:flex-none"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copiar
                  </Button>
                  <Button
                    onClick={() => window.open('/agendar', '_blank')}
                    className="flex-1 sm:flex-none bg-[#0891B2] hover:bg-[#0e7490]"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Abrir
                  </Button>
                </div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Características
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Interfaz simple y elegante optimizada para móvil</li>
                  <li>• Flujo de 3 pasos: Fecha → Hora → Datos</li>
                  <li>• Muestra solo horarios disponibles en tiempo real</li>
                  <li>• No requiere registro ni inicio de sesión</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Horarios Tab */}
        <TabsContent value="schedule" className="space-y-6">
          {/* Weekly Schedule */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-[#0891B2]" />
                Horario Semanal
              </CardTitle>
              <CardDescription>
                Define los horarios de atención para cada día de la semana
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {rules.map((rule) => (
                <div 
                  key={rule.weekday}
                  className={`flex flex-col md:flex-row md:items-center gap-4 p-4 rounded-lg transition-colors ${
                    rule.is_active ? 'bg-slate-50' : 'bg-slate-100 opacity-60'
                  }`}
                  data-testid={`rule-day-${rule.weekday}`}
                >
                  <div className="flex items-center gap-3 md:w-40">
                    <Switch
                      checked={rule.is_active}
                      onCheckedChange={(checked) => handleRuleChange(rule.weekday, 'is_active', checked)}
                      data-testid={`toggle-day-${rule.weekday}`}
                    />
                    <span className="font-medium">
                      {WEEKDAYS.find(d => d.value === rule.weekday)?.label}
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-4 flex-1">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-slate-500">Desde</Label>
                      <Input
                        type="time"
                        value={rule.start_time}
                        onChange={(e) => handleRuleChange(rule.weekday, 'start_time', e.target.value)}
                        disabled={!rule.is_active}
                        className="w-32"
                      />
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-slate-500">Hasta</Label>
                      <Input
                        type="time"
                        value={rule.end_time}
                        onChange={(e) => handleRuleChange(rule.weekday, 'end_time', e.target.value)}
                        disabled={!rule.is_active}
                        className="w-32"
                      />
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500" data-testid={`rule-slot-label-${rule.weekday}`}>Espacios de 10 min</span>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Blocked Slots */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Ban className="h-5 w-5 text-red-500" />
                  Bloqueos
                </CardTitle>
                <CardDescription>
                  Días u horarios específicos sin disponibilidad
                </CardDescription>
              </div>
              <Button 
                onClick={() => setShowBlockDialog(true)}
                variant="outline"
                data-testid="add-block-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar Bloqueo
              </Button>
            </CardHeader>
            <CardContent>
              {blockedSlots.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">No hay bloqueos configurados</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {blockedSlots.map((block) => (
                    <div 
                      key={block.id}
                      className="flex items-center justify-between p-4 bg-red-50 border border-red-100 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                          <Ban className="h-5 w-5 text-red-500" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {format(new Date(block.block_date + 'T12:00:00'), "EEEE, d 'de' MMMM", { locale: es })}
                          </p>
                          <p className="text-sm text-slate-500">
                            {block.start_time} - {block.end_time}
                            {block.reason && ` • ${block.reason}`}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteBlock(block.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-100"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-6">
          {/* Google Calendar */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#0891B2]" />
                Google Calendar
              </CardTitle>
              <CardDescription>
                Sincroniza tus citas con Google Calendar para verlas en tu calendario personal
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Paso 1: Configurar Credenciales */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    googleCalendarConfig.configured ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'
                  }`}>
                    1
                  </div>
                  <h4 className="font-medium text-slate-900">Configurar Credenciales de Google</h4>
                </div>
                
                {googleCalendarConfig.configured ? (
                  <div className="ml-8 space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                      <div className="flex-1">
                        <p className="font-medium text-emerald-700">Credenciales configuradas</p>
                        <p className="text-sm text-emerald-600">Client ID: {googleCalendarConfig.client_id}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowGoogleConfigDialog(true)}
                        className="text-slate-600"
                      >
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={deleteGoogleCalendarConfig}
                        className="text-red-500 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="ml-8 space-y-3">
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <p className="text-sm text-slate-600 mb-3">
                        Para conectar Google Calendar, necesitas crear credenciales OAuth en Google Cloud Console:
                      </p>
                      <ol className="list-decimal list-inside text-sm text-slate-600 space-y-1 mb-4">
                        <li>Ve a <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-[#0891B2] hover:underline">Google Cloud Console</a></li>
                        <li>Crea un proyecto o selecciona uno existente</li>
                        <li>Habilita la API de Google Calendar</li>
                        <li>Ve a Credenciales → Crear credenciales → ID de cliente OAuth</li>
                        <li>Selecciona "Aplicación web"</li>
                        <li>Agrega este URI de redirección: <code className="bg-slate-200 px-2 py-0.5 rounded text-xs">{window.location.origin}/api/oauth/calendar/callback</code></li>
                      </ol>
                      <Button
                        onClick={() => setShowGoogleConfigDialog(true)}
                        className="bg-[#0891B2] hover:bg-[#0e7490]"
                        data-testid="configure-google-credentials"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Ingresar Credenciales
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Paso 2: Conectar Cuenta */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    googleCalendarStatus.connected ? 'bg-emerald-500 text-white' : googleCalendarConfig.configured ? 'bg-slate-200 text-slate-600' : 'bg-slate-100 text-slate-400'
                  }`}>
                    2
                  </div>
                  <h4 className={`font-medium ${googleCalendarConfig.configured ? 'text-slate-900' : 'text-slate-400'}`}>
                    Conectar Cuenta de Google
                  </h4>
                </div>
                
                {googleCalendarConfig.configured && (
                  <div className="ml-8">
                    {googleCalendarStatus.connected ? (
                      <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-emerald-500" />
                        <div className="flex-1">
                          <p className="font-medium text-emerald-700">Cuenta conectada</p>
                          <p className="text-sm text-emerald-600">{googleCalendarStatus.email}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={disconnectGoogleCalendar}
                          className="text-red-500 border-red-200 hover:bg-red-50"
                        >
                          <Unlink className="h-4 w-4 mr-2" />
                          Desconectar
                        </Button>
                      </div>
                    ) : (
                      <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                        <p className="text-sm text-slate-600 mb-3">
                          Autoriza el acceso a tu cuenta de Google para sincronizar citas automáticamente.
                        </p>
                        <Button
                          onClick={connectGoogleCalendar}
                          className="bg-[#0891B2] hover:bg-[#0e7490]"
                          data-testid="connect-google-calendar"
                        >
                          <Link2 className="h-4 w-4 mr-2" />
                          Conectar Google Calendar
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Estado Final */}
              {googleCalendarStatus.connected && (
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-sm text-emerald-600 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Las nuevas citas se sincronizarán automáticamente con tu Google Calendar.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Channels Tab */}
        <TabsContent value="channels" className="space-y-6">
          {/* WhatsApp */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-emerald-500" />
                WhatsApp Business
              </CardTitle>
              <CardDescription>
                Permite a los pacientes agendar citas y conversar con la secretaria virtual por WhatsApp
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className={`flex items-center gap-3 p-4 rounded-lg border ${
                  whatsappStatus.connected 
                    ? 'bg-emerald-50 border-emerald-200' 
                    : 'bg-amber-50 border-amber-200'
                }`}>
                  <MessageSquare className={`h-5 w-5 ${
                    whatsappStatus.connected ? 'text-emerald-500' : 'text-amber-500'
                  }`} />
                  <div className="flex-1">
                    <p className={`font-medium ${
                      whatsappStatus.connected ? 'text-emerald-700' : 'text-amber-700'
                    }`}>
                      {whatsappStatus.connected ? 'Conectado' : 'Configuración Pendiente'}
                    </p>
                    <p className={`text-sm ${
                      whatsappStatus.connected ? 'text-emerald-600' : 'text-amber-600'
                    }`}>
                      {whatsappStatus.connected 
                        ? whatsappStatus.phone_number || 'WhatsApp activo'
                        : 'Requiere configuración del servicio de WhatsApp'}
                    </p>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-medium text-slate-900">Funciones disponibles:</h4>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                      Agendamiento de citas por conversación
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                      Secretaria virtual con IA (GPT-5.2)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                      Confirmación automática de citas
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                      Recordatorios antes de la cita
                    </li>
                  </ul>
                </div>

                <div className="text-sm text-slate-500">
                  <p>Para activar WhatsApp Business, el servicio de Baileys debe estar configurado.</p>
                  <a 
                    href="https://github.com/WhiskeySockets/Baileys" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[#0891B2] hover:underline mt-1"
                  >
                    Ver documentación <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Agenda Compacta - Mobile Booking Link */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Share2 className="h-5 w-5 text-violet-500" />
                Agenda Compacta (Link Compartible)
              </CardTitle>
              <CardDescription>
                Link optimizado para móviles que puedes compartir con tus pacientes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
                <div className="flex-1">
                  <p className="font-medium text-emerald-700">Activo</p>
                  <p className="text-sm text-emerald-600">
                    Interfaz visual en 3 pasos: Fecha → Hora → Datos
                  </p>
                </div>
                <a href="/agendar" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Ver
                  </Button>
                </a>
              </div>

              {/* Shareable Link Section */}
              <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-5">
                <h4 className="font-semibold text-violet-900 mb-3 flex items-center gap-2">
                  <Link2 className="h-4 w-4" />
                  Link para compartir
                </h4>
                <div className="flex gap-2">
                  <Input 
                    readOnly 
                    value={`${window.location.origin}/agendar`}
                    className="bg-white text-sm font-mono"
                  />
                  <Button 
                    variant="outline"
                    className="shrink-0 border-violet-300 hover:bg-violet-100"
                    onClick={() => {
                      const link = `${window.location.origin}/agendar`;
                      if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(link).then(() => {
                          alert('Link copiado al portapapeles');
                        }).catch(() => {
                          // Fallback
                          const textArea = document.createElement('textarea');
                          textArea.value = link;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                          alert('Link copiado al portapapeles');
                        });
                      } else {
                        // Fallback for older browsers
                        const textArea = document.createElement('textarea');
                        textArea.value = link;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        alert('Link copiado al portapapeles');
                      }
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copiar
                  </Button>
                </div>
                <p className="text-xs text-violet-600 mt-3">
                  Comparte este link por WhatsApp, SMS, email o redes sociales
                </p>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-slate-900">Características:</h4>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    Diseño optimizado para móviles
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    Flujo simple en 3 pasos
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    Campos: Primer Nombre, Segundo Nombre, Apellidos
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    Permite cambiar fecha/hora antes de confirmar
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    No requiere inicio de sesión
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Block Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71]">Agregar Bloqueo</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddBlock} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha inicio</Label>
                <Input
                  type="date"
                  value={newBlock.block_date_start}
                  onChange={(e) => setNewBlock({ ...newBlock, block_date_start: e.target.value })}
                  required
                  data-testid="block-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Fecha fin (opcional)</Label>
                <Input
                  type="date"
                  value={newBlock.block_date_end}
                  onChange={(e) => setNewBlock({ ...newBlock, block_date_end: e.target.value })}
                  min={newBlock.block_date_start}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hora inicio</Label>
                <Input
                  type="time"
                  value={newBlock.start_time}
                  onChange={(e) => setNewBlock({ ...newBlock, start_time: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Hora fin</Label>
                <Input
                  type="time"
                  value={newBlock.end_time}
                  onChange={(e) => setNewBlock({ ...newBlock, end_time: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Motivo (opcional)</Label>
              <Textarea
                value={newBlock.reason}
                onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })}
                rows={2}
                placeholder="Ej: Día feriado, Vacaciones, etc."
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowBlockDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-[#0891B2] hover:bg-[#0e7490]" data-testid="create-block-button">
                Crear Bloqueo
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Google Calendar Config Dialog */}
      <Dialog open={showGoogleConfigDialog} onOpenChange={setShowGoogleConfigDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71]">Configurar Google Calendar</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveGoogleCalendarConfig} className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                Obtén estas credenciales desde <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="font-medium underline">Google Cloud Console</a>
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="client_id">Client ID</Label>
              <Input
                id="client_id"
                value={googleCredentials.client_id}
                onChange={(e) => setGoogleCredentials({ ...googleCredentials, client_id: e.target.value })}
                placeholder="xxxxx.apps.googleusercontent.com"
                required
                data-testid="google-client-id-input"
              />
              <p className="text-xs text-slate-500">El ID de cliente OAuth 2.0</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_secret">Client Secret</Label>
              <Input
                id="client_secret"
                type="password"
                value={googleCredentials.client_secret}
                onChange={(e) => setGoogleCredentials({ ...googleCredentials, client_secret: e.target.value })}
                placeholder="GOCSPX-xxxxxxxxxxxx"
                required
                data-testid="google-client-secret-input"
              />
              <p className="text-xs text-slate-500">El secreto del cliente OAuth 2.0</p>
            </div>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700 font-medium mb-1">URI de Redirección</p>
              <p className="text-sm text-amber-600 mb-2">Asegúrate de agregar este URI en la configuración de tu aplicación OAuth en Google Cloud:</p>
              <code className="block p-2 bg-white border border-amber-200 rounded text-xs break-all">
                {window.location.origin}/api/oauth/calendar/callback
              </code>
            </div>

            <div className="flex gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1" 
                onClick={() => {
                  setShowGoogleConfigDialog(false);
                  setGoogleCredentials({ client_id: '', client_secret: '' });
                }}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-[#0891B2] hover:bg-[#0e7490]" 
                disabled={savingGoogleConfig}
                data-testid="save-google-config-button"
              >
                {savingGoogleConfig ? 'Guardando...' : 'Guardar Credenciales'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AvailabilityPage;
